/**
 * Saves application state to sessionStorage with automatic serialization.
 *
 * This function handles server-side rendering compatibility by checking for window availability
 * and provides error handling for sessionStorage operations that might fail due to quota limits
 * or disabled storage.
 *
 * @param state - The state object to be serialized and stored
 * @param key - The sessionStorage key under which to store the state
 * @template T - Must extend object to ensure JSON serialization compatibility
 */
export const setStateToSessionStorage = <T extends object>(state: T, key: string) => {
  // Ensure we're running in browser environment (not during SSR)
  if (typeof window !== "undefined") {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Silently handle sessionStorage errors (quota exceeded, disabled storage, etc.)
      return;
    }
  }
};

/**
 * Removes state from sessionStorage with SSR compatibility.
 *
 * Safely removes an item from sessionStorage while handling potential errors
 * and ensuring compatibility with server-side rendering environments.
 *
 * @param key - The sessionStorage key to remove
 */
export const removeStateFromSessionStorage = (key: string) => {
  // Ensure we're running in browser environment (not during SSR)
  if (typeof window !== "undefined") {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Silently handle sessionStorage errors (access denied, etc.)
      return;
    }
  }
};

/**
 * Retrieves and deserializes state from sessionStorage with comprehensive error handling.
 *
 * This function handles multiple failure scenarios:
 * - SSR environments where sessionStorage is unavailable
 * - Missing or null values in sessionStorage
 * - JSON parsing errors from corrupted data
 * - sessionStorage access restrictions
 *
 * @param key - The sessionStorage key to retrieve
 * @returns The deserialized state object or undefined if retrieval/parsing fails
 * @template T - Must extend object to match the expected return type structure
 */
export const getStateFromSessionStorage = <T extends object>(key: string): T | undefined => {
  // Ensure we're running in browser environment (not during SSR)
  if (typeof window !== "undefined") {
    try {
      const stringState = sessionStorage.getItem(key);

      // Return undefined for missing or null values
      if (!stringState) {
        return undefined;
      }

      // Attempt to parse the stored JSON string
      return JSON.parse(stringState);
    } catch {
      // Handle JSON parsing errors or sessionStorage access issues
      return undefined;
    }
  }

  // Return undefined in SSR environment
  return undefined;
};
